import { useCallback, useState } from "react";

import { ChevronRight } from "components/icons/generated";
import RunPriorityMenuItems from "components/Run/elements/RunPriorityMenuItems";
import FlashContext from "components/FlashMessages/FlashContext";
import Button from "ds/components/Button";
import DropdownMenu from "ds/components/DropdownMenu";
import useTypedContext from "hooks/useTypedContext";
import { RUN_PRIORITY_PRESETS } from "shared/Run/priorityPresets";
import useSetRunPriority from "shared/Run/useSetRunPriority";
import { RunPriorityPreset } from "types/generated";
import { AccessResult } from "utils/access";

import { useRunShortcut } from "../../hooks/useRunShortcut";
import styles from "./styles.module.css";

type RunPriorityMenuProps = {
  runId: string;
  stackId: string;
  priorityPreset: RunPriorityPreset;
  canPrioritize: AccessResult;
};

const RunPriorityMenu = ({
  runId,
  stackId,
  priorityPreset,
  canPrioritize,
}: RunPriorityMenuProps) => {
  const { reportSuccess } = useTypedContext(FlashContext);
  const [isOpen, setIsOpen] = useState(false);

  const [setRunPriority, { loading }] = useSetRunPriority();

  const handleSelect = useCallback(
    (preset: RunPriorityPreset) =>
      setRunPriority({ stack: stackId, run: runId, preset }, (data) => {
        if (data?.runPrioritySet) {
          const { label } = RUN_PRIORITY_PRESETS[data.runPrioritySet.priorityPreset];

          reportSuccess({ message: `Run ${runId} was set to ${label}` });
        }
      }),
    [setRunPriority, stackId, runId, reportSuccess]
  );

  useRunShortcut({
    label: "Set priority",
    code: "mod+alt+h",
    callback: () => setIsOpen(true),
    loading,
    disabled: !canPrioritize.result,
  });

  if (!canPrioritize.result && !canPrioritize.message) {
    return null;
  }

  return (
    <DropdownMenu
      isOpen={isOpen}
      onOpenChange={setIsOpen}
      triggerComponent={
        <Button
          className={styles.trigger}
          variant="secondary"
          size="small"
          active={isOpen}
          disabled={loading}
          loading={loading}
          endIcon={ChevronRight}
          endIconRotate={isOpen ? "270" : "90"}
        >
          {RUN_PRIORITY_PRESETS[priorityPreset].label}
        </Button>
      }
    >
      <RunPriorityMenuItems
        current={priorityPreset}
        onSelect={handleSelect}
        canPrioritize={canPrioritize}
      />
    </DropdownMenu>
  );
};

export default RunPriorityMenu;
